import { Link } from 'react-router-dom';
import { useSettingsStore } from '@/store/settingsStore';
import { useRewardStore } from '@/store/rewardStore';
import { games } from '@/data/games';

/** Einstellungen — Sound, Dark Mode, Stats-Reset */
export default function SettingsPage() {
  const { darkMode, soundEnabled, toggleDarkMode, toggleSound } = useSettingsStore();
  const { progress, resetAll } = useRewardStore();

  const totalStars = Object.values(progress).reduce((sum, p) => sum + p.totalStars, 0);
  const totalRounds = Object.values(progress).reduce((sum, p) => sum + p.roundsPlayed, 0);

  return (
    <div className="mx-auto max-w-2xl px-6 py-8" data-testid="settings-page">
      <header className="mb-8 flex items-center gap-4">
        <Link
          to="/"
          className="rounded-lg bg-white px-4 py-2 shadow-sm transition hover:shadow-md dark:bg-gray-700"
          aria-label="Zurück zur Auswahl"
          data-testid="back-button"
        >
          ←
        </Link>
        <h1 className="text-2xl font-bold text-indigo-600 dark:text-indigo-300">Einstellungen</h1>
      </header>

      {/* Toggles */}
      <section className="mb-8 rounded-2xl bg-white p-6 shadow-sm dark:bg-gray-800">
        <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-100">
          Anzeige & Ton
        </h2>

        <div className="flex items-center justify-between py-3">
          <div>
            <p className="font-medium text-gray-700 dark:text-gray-200">Dark Mode</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Schöner für abends</p>
          </div>
          <button
            onClick={toggleDarkMode}
            className="relative h-8 w-14 rounded-full transition-colors"
            style={{ backgroundColor: darkMode ? '#6366f1' : '#d1d5db' }}
            aria-label={darkMode ? 'Dark Mode ausschalten' : 'Dark Mode einschalten'}
            aria-pressed={darkMode}
            data-testid="settings-dark-toggle"
          >
            <span
              className="absolute top-1 h-6 w-6 rounded-full bg-white transition-transform"
              style={{ transform: darkMode ? 'translateX(28px)' : 'translateX(4px)' }}
            />
          </button>
        </div>

        <div className="flex items-center justify-between py-3">
          <div>
            <p className="font-medium text-gray-700 dark:text-gray-200">Sound</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              In Schulen besser aus — default: aus
            </p>
          </div>
          <button
            onClick={toggleSound}
            className="relative h-8 w-14 rounded-full transition-colors"
            style={{ backgroundColor: soundEnabled ? '#6366f1' : '#d1d5db' }}
            aria-label={soundEnabled ? 'Sound ausschalten' : 'Sound einschalten'}
            aria-pressed={soundEnabled}
            data-testid="settings-sound-toggle"
          >
            <span
              className="absolute top-1 h-6 w-6 rounded-full bg-white transition-transform"
              style={{ transform: soundEnabled ? 'translateX(28px)' : 'translateX(4px)' }}
            />
          </button>
        </div>
      </section>

      {/* Stats */}
      <section className="mb-8 rounded-2xl bg-white p-6 shadow-sm dark:bg-gray-800">
        <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-100">
          Spielstatistik
        </h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-xl bg-indigo-50 p-4 text-center dark:bg-gray-700">
            <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-300">
              ⭐ {totalStars}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Sterne gesamt</p>
          </div>
          <div className="rounded-xl bg-purple-50 p-4 text-center dark:bg-gray-700">
            <div className="text-3xl font-bold text-purple-600 dark:text-purple-300">
              🎮 {totalRounds}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Runden gespielt</p>
          </div>
        </div>

        {/* Pro-Spiel Stats */}
        {games.map((game) => {
          const p = progress[game.id];
          if (!p) return null;
          return (
            <div
              key={game.id}
              className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3 dark:border-gray-700"
            >
              <span className="font-medium text-gray-700 dark:text-gray-200">
                {game.icon} {game.title}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                ⭐ {p.totalStars} · 🏆 {p.roundsWon} · Best: {p.highScore}
              </span>
            </div>
          );
        })}
      </section>

      {/* Reset */}
      <section className="rounded-2xl bg-white p-6 shadow-sm dark:bg-gray-800">
        <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-100">Daten</h2>
        <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
          Alle Spielstatistiken und Einstellungen werden lokal im Browser gespeichert.
        </p>
        <button
          onClick={() => {
            if (confirm('Wirklich alle Statistiken zurücksetzen?')) {
              resetAll();
            }
          }}
          className="rounded-xl bg-red-100 px-6 py-3 font-semibold text-red-600 transition hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400"
          data-testid="reset-stats-button"
        >
          Statistiken zurücksetzen
        </button>
      </section>
    </div>
  );
}
